import { useAuthStore } from '../stores/auth.js'
import Holidays from 'date-holidays'

// Swedish holidays helper
class SwedishHolidays {
  constructor() {
    this.holidays = new Holidays('SE') // Sweden
  }

  // Check if a given date is a Swedish national holiday
  isHoliday(date) {
    const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0]
    const year = new Date(dateStr).getFullYear()
    const holidayList = this.holidays.getHolidays(year)
    
    return holidayList.some(holiday => {
      // Only count public holidays, not observances
      if (holiday.type !== 'public') return false
      
      // holiday.date is a string like "2025-11-01 00:00:00"
      // Extract just the date part (YYYY-MM-DD)
      let holidayDate
      if (typeof holiday.date === 'string') {
        holidayDate = holiday.date.split(' ')[0] // Get "2025-11-01" from "2025-11-01 00:00:00"
      } else if (holiday.date instanceof Date) {
        holidayDate = holiday.date.toISOString().split('T')[0]
      } else {
        // Assume it's a timestamp
        holidayDate = new Date(holiday.date).toISOString().split('T')[0]
      }
      return holidayDate === dateStr
    })
  }

  // Get holiday name for a given date (if it's a holiday)
  getHolidayName(date) {
    const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0]
    const year = new Date(dateStr).getFullYear()
    const holidayList = this.holidays.getHolidays(year)
    
    const holiday = holidayList.find(holiday => {
      // Only count public holidays, not observances
      if (holiday.type !== 'public') return false
      
      // holiday.date is a string like "2025-11-01 00:00:00"
      // Extract just the date part (YYYY-MM-DD)
      let holidayDate
      if (typeof holiday.date === 'string') {
        holidayDate = holiday.date.split(' ')[0] // Get "2025-11-01" from "2025-11-01 00:00:00"
      } else if (holiday.date instanceof Date) {
        holidayDate = holiday.date.toISOString().split('T')[0]
      } else {
        // Assume it's a timestamp
        holidayDate = new Date(holiday.date).toISOString().split('T')[0]
      }
      return holidayDate === dateStr
    })
    
    return holiday ? holiday.name : null
  }

  // Get all holidays for a given year
  getHolidaysForYear(year) {
    return this.holidays.getHolidays(year).map(holiday => {
      // Handle different date formats - usually "YYYY-MM-DD HH:MM:SS"
      let dateStr
      if (typeof holiday.date === 'string') {
        dateStr = holiday.date.split(' ')[0] // Get "2025-11-01" from "2025-11-01 00:00:00"
      } else if (holiday.date instanceof Date) {
        dateStr = holiday.date.toISOString().split('T')[0]
      } else {
        dateStr = new Date(holiday.date).toISOString().split('T')[0]
      }
      
      return {
        date: dateStr,
        name: holiday.name,
        type: holiday.type
      }
    })
  }

  // Get holidays for a specific month
  getHolidaysForMonth(year, month) {
    const allHolidays = this.getHolidaysForYear(year)
    return allHolidays.filter(holiday => {
      const holidayMonth = new Date(holiday.date).getMonth() + 1
      return holidayMonth === month
    })
  }
}

// Create singleton instance
const swedishHolidays = new SwedishHolidays()

export const obHoursApi = {
  // Save daily OB hours with breakdown (OB1, OB2, OB3)
  // Backend handles upsert based on user+date unique constraint
  async saveOBHours(userId, date, ob1Hours = 0, ob2Hours = 0, ob3Hours = 0) {
    const authStore = useAuthStore()
    const response = await authStore.makeAuthenticatedRequest('/api/ob-hours/', {
      method: 'POST',
      body: JSON.stringify({
        user: userId,
        date,  // Format: 'YYYY-MM-DD'
        ob1_hours: ob1Hours,
        ob2_hours: ob2Hours,
        ob3_hours: ob3Hours
      })
    })
    return await response.json()
  },

  // Get daily OB hours for a specific date
  async getDailyOBHours(userId, date) {
    const authStore = useAuthStore()
    const response = await authStore.makeAuthenticatedRequest(
      `/api/ob-hours/?user=${userId}&date=${date}`
    )
    const data = await response.json()
    return data.results?.[0] || null
  },

  // Get all daily OB hours for a month and return both raw records and aggregated monthly total
  async getMonthlyOBHours(userId, year, month) {
    const authStore = useAuthStore()
    const url = `/api/ob-hours/?user=${userId}&year=${year}&month=${month}`
    
    const response = await authStore.makeAuthenticatedRequest(url)
    const data = await response.json()
    
    // Handle both response formats:
    // 1. Array directly: [{date: "2025-10-01", ob1_hours: 1.75, ...}, ...]
    // 2. Object with results: {results: [...], count: 6}
    let records = []
    if (Array.isArray(data)) {
      // Backend returns array directly
      records = data
    } else if (data && data.results) {
      // Backend returns object with results property
      records = data.results
    }
    
    // Calculate monthly totals from daily records
    if (records.length > 0) {
      const monthlyTotal = records.reduce((acc, day) => {
        return {
          ob1_hours: acc.ob1_hours + (day.ob1_hours || 0),
          ob2_hours: acc.ob2_hours + (day.ob2_hours || 0),
          ob3_hours: acc.ob3_hours + (day.ob3_hours || 0),
          total_hours: acc.total_hours + (day.total_hours || 0)
        }
      }, { ob1_hours: 0, ob2_hours: 0, ob3_hours: 0, total_hours: 0 })
      
      return { 
        results: records,
        monthlyTotal  // Aggregated monthly totals for easy access
      }
    }
    return { results: [], monthlyTotal: { ob1_hours: 0, ob2_hours: 0, ob3_hours: 0, total_hours: 0 } }
  },

  // Get OB hours for multiple users (for manager view)
  async getOBHoursForUsers(userIds, year, month) {
    const authStore = useAuthStore()
    const userIdsParam = userIds.join(',')
    const response = await authStore.makeAuthenticatedRequest(
      `/api/ob-hours/?user_ids=${userIdsParam}&year=${year}&month=${month}`
    )
    const data = await response.json()
    
    // Group by user and aggregate
    const userTotals = {}
    if (data && data.results) {
      data.results.forEach(record => {
        if (!userTotals[record.user]) {
          userTotals[record.user] = {
            ob1_hours: 0,
            ob2_hours: 0,
            ob3_hours: 0,
            total_hours: 0
          }
        }
        userTotals[record.user].ob1_hours += record.ob1_hours || 0
        userTotals[record.user].ob2_hours += record.ob2_hours || 0
        userTotals[record.user].ob3_hours += record.ob3_hours || 0
        userTotals[record.user].total_hours += record.total_hours || 0
      })
    }
    
    return { ...data, userTotals }
  },

  // Holiday-related methods
  
  // Check if a date is a Swedish national holiday
  isSwedishHoliday(date) {
    const result = swedishHolidays.isHoliday(date)
    return result
  },

  // Get the name of Swedish holiday for a given date
  getSwedishHolidayName(date) {
    const name = swedishHolidays.getHolidayName(date)
    return name
  },

  // Get all Swedish holidays for a specific year
  getSwedishHolidays(year) {
    return swedishHolidays.getHolidaysForYear(year)
  },

  // Get Swedish holidays for a specific month
  getSwedishHolidaysForMonth(year, month) {
    return swedishHolidays.getHolidaysForMonth(year, month)
  },

  // Debug: Test holiday detection for a specific date
  debugHoliday(date) {
    
    // Get all holidays for the year to see what's available
    const year = new Date(date).getFullYear()
    const allHolidays = this.getSwedishHolidays(year)
    
    // Get holidays for the month
    const month = new Date(date).getMonth() + 1
    const monthHolidays = this.getSwedishHolidaysForMonth(year, month)
    
    return {
      isHoliday: this.isSwedishHoliday(date),
      name: this.getSwedishHolidayName(date),
      allHolidays,
      monthHolidays
    }
  },

  // Calculate OB hours based on work hours and date (considering holidays)
  calculateOBHours(workStartTime, workEndTime, date, totalWorkedHours, breakDuration = 0) {
    
    const isHoliday = this.isSwedishHoliday(date)
    
    // If it's a Swedish national holiday, all hours are OB3
    if (isHoliday) {
      const holidayName = this.getSwedishHolidayName(date)
      return {
        ob1_hours: 0,
        ob2_hours: 0,
        ob3_hours: totalWorkedHours,
        total_hours: totalWorkedHours,
        holiday_name: holidayName,
        is_holiday: true
      }
    }

    // Regular day calculation based on time periods
    const result = {
      ob1_hours: 0,
      ob2_hours: 0,
      ob3_hours: 0,
      total_hours: totalWorkedHours,
      is_holiday: false
    }

    // Parse time strings (format: "HH:MM" or "HH:MM:SS")
    const parseTime = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number)
      return hours + minutes / 60
    }

    const startHour = parseTime(workStartTime)
    const endHour = parseTime(workEndTime)
    
    // Calculate total shift duration (including break)
    let shiftDuration = endHour - startHour
    if (shiftDuration < 0) shiftDuration += 24 // Handle overnight shifts

    // Swedish OB time definitions (hardcoded):
    // OB1 (Kväll/Evening): 18:15-20:00 on weekdays
    // OB2 (Natt/Night): 20:00-05:00 daily
    // OB3 (Helg/Weekend): Saturday 12:00 - Monday 05:00
    const ob1Start = 18.25 // 18:15
    const ob1End = 20
    const ob2Start = 20
    const ob2End = 5
    const ob3StartDay = 6 // Saturday
    const ob3StartTime = 12
    const ob3EndDay = 1 // Monday
    const ob3EndTime = 5
    const minShiftForBreak = 5.5

    const date_obj = new Date(date)
    const dayOfWeek = date_obj.getDay() // 0=Sunday, 6=Saturday
    
    // Calculate when break occurs (after minShiftForBreak hours if shift >= minShiftForBreak)
    let breakStart = null
    let breakEnd = null
    if (shiftDuration >= minShiftForBreak && breakDuration > 0) {
      const breakStartHour = startHour + minShiftForBreak
      breakStart = breakStartHour
      breakEnd = breakStartHour + (breakDuration / 60)
    }

    // Helper to calculate overlap between two time ranges
    const calculateOverlap = (range1Start, range1End, range2Start, range2End) => {
      const overlapStart = Math.max(range1Start, range2Start)
      const overlapEnd = Math.min(range1End, range2End)
      return Math.max(0, overlapEnd - overlapStart)
    }

    // Check if we're in OB3 weekend period (using settings)
    const isWeekendOB3 = (
      (dayOfWeek > ob3StartDay || (dayOfWeek === ob3StartDay && endHour > ob3StartTime)) &&
      (dayOfWeek < ob3EndDay || (dayOfWeek === ob3EndDay && endHour <= ob3EndTime))
    )

    if (isWeekendOB3) {
      if (dayOfWeek > ob3StartDay && dayOfWeek < ob3EndDay) {
        // Full day within weekend period (e.g., Sunday) - all hours are OB3
        result.ob3_hours = totalWorkedHours
        return result
      } else if (dayOfWeek === ob3StartDay) {
        // Start day (e.g., Saturday) - OB3 starts at ob3StartTime
        if (startHour >= ob3StartTime) {
          // Entire shift after OB3 start - all OB3
          result.ob3_hours = totalWorkedHours
          return result
        } else if (endHour <= ob3StartTime) {
          // Entire shift before OB3 start - treat as weekday (fall through)
        } else {
          // Shift spans OB3 start - calculate OB3 portion
          let ob3Hours = calculateOverlap(startHour, endHour, ob3StartTime, 24)
          
          // Deduct break if it falls during OB3 period
          if (breakStart && breakStart >= ob3StartTime) {
            const breakOverlap = calculateOverlap(breakStart, breakEnd, ob3StartTime, 24)
            ob3Hours -= breakOverlap
          }
          
          result.ob3_hours = Math.max(0, ob3Hours)
          
          // Hours before OB3 start follow weekday rules
          const weekdayHours = totalWorkedHours - result.ob3_hours
          
          // OB2 (Night): using settings
          let ob2Hours = 0
          if (startHour < ob2End) {
            ob2Hours += calculateOverlap(startHour, Math.min(endHour, ob3StartTime), 0, ob2End)
            // Deduct break if it falls during OB2 period
            if (breakStart && breakStart < ob2End) {
              const breakOverlap = calculateOverlap(breakStart, breakEnd, 0, ob2End)
              ob2Hours -= breakOverlap
            }
          }
          result.ob2_hours = Math.min(weekdayHours, Math.max(0, ob2Hours))
          
          return result
        }
      } else if (dayOfWeek === ob3EndDay) {
        // End day (e.g., Monday) - OB3 ends at ob3EndTime
        if (endHour <= ob3EndTime) {
          result.ob3_hours = totalWorkedHours
          return result
        } else if (startHour >= ob3EndTime) {
          // Shift starts after OB3 end - treat as weekday (fall through)
        } else {
          // Shift spans OB3 end
          let ob3Hours = calculateOverlap(startHour, endHour, 0, ob3EndTime)
          
          // Deduct break if it falls during OB3 period
          if (breakStart && breakStart < ob3EndTime) {
            const breakOverlap = calculateOverlap(breakStart, breakEnd, 0, ob3EndTime)
            ob3Hours -= breakOverlap
          }
          
          result.ob3_hours = Math.max(0, ob3Hours)
          
          // Hours after OB3 end follow weekday rules
          const weekdayHours = totalWorkedHours - result.ob3_hours
          
          // OB1 (Evening): using settings
          let ob1Hours = 0
          if (endHour > ob1Start && startHour < ob1End) {
            ob1Hours = calculateOverlap(Math.max(startHour, ob3EndTime), endHour, ob1Start, ob1End)
            // Deduct break if it falls during OB1 period
            if (breakStart && breakStart >= ob1Start && breakStart < ob1End) {
              const breakOverlap = calculateOverlap(breakStart, breakEnd, ob1Start, ob1End)
              ob1Hours -= breakOverlap
            }
          }
          result.ob1_hours = Math.min(weekdayHours, Math.max(0, ob1Hours))
          
          // OB2 (Night): using settings (only the evening part since morning covered by OB3)
          let ob2Hours = 0
          if (endHour > ob2Start) {
            ob2Hours = calculateOverlap(Math.max(startHour, ob3EndTime), endHour, ob2Start, 24)
            // Deduct break if it falls during OB2 period
            if (breakStart && breakStart >= ob2Start) {
              const breakOverlap = calculateOverlap(breakStart, breakEnd, ob2Start, 24)
              ob2Hours -= breakOverlap
            }
          }
          result.ob2_hours = Math.min(weekdayHours - result.ob1_hours, Math.max(0, ob2Hours))
          
          return result
        }
      }
    }

    // Weekday calculation (after OB3 end - before OB3 start)
    // OB2 (Night): using settings
    let ob2Hours = 0
    if (startHour < ob2End) {
      ob2Hours += calculateOverlap(startHour, endHour, 0, ob2End)
      // Deduct break if it falls during morning OB2
      if (breakStart && breakStart < ob2End) {
        const breakOverlap = calculateOverlap(breakStart, breakEnd, 0, ob2End)
        ob2Hours -= breakOverlap
      }
    }
    if (endHour > ob2Start) {
      ob2Hours += calculateOverlap(startHour, endHour, ob2Start, 24)
      // Deduct break if it falls during evening OB2
      if (breakStart && breakStart >= ob2Start) {
        const breakOverlap = calculateOverlap(breakStart, breakEnd, ob2Start, 24)
        ob2Hours -= breakOverlap
      }
    }
    result.ob2_hours = Math.max(0, ob2Hours)

    // OB1 (Evening): using settings
    let ob1Hours = 0
    if (endHour > ob1Start && startHour < ob1End) {
      ob1Hours = calculateOverlap(startHour, endHour, ob1Start, ob1End)
      // Deduct break if it falls during OB1 period
      if (breakStart && breakStart >= ob1Start && breakStart < ob1End) {
        const breakOverlap = calculateOverlap(breakStart, breakEnd, ob1Start, ob1End)
        ob1Hours -= breakOverlap
      }
    }
    result.ob1_hours = Math.min(totalWorkedHours - result.ob2_hours, Math.max(0, ob1Hours))

    return result
  },

  // Save OB hours with automatic holiday detection
  async saveOBHoursAuto(userId, date, workStartTime, workEndTime, totalWorkedHours) {
    const obCalculation = this.calculateOBHours(workStartTime, workEndTime, date, totalWorkedHours)
    
    return await this.saveOBHours(
      userId, 
      date, 
      obCalculation.ob1_hours, 
      obCalculation.ob2_hours, 
      obCalculation.ob3_hours
    )
  }
}

// Expose for debugging in dev mode
if (import.meta.env.DEV) {
  window.obHoursApi = obHoursApi
}